import React from 'react';
import Link from 'next/link';
import { EdgeBadge } from '@benzinga/core-ui';
import { ShareButtons } from '@benzinga/ui';
import styles from './ProfileHero.module.scss';
import { GovTradesTabs } from '../Hero/GovTradesTabs';
import type { GovTradesTab } from '../Hero/getActiveGovTradesTab';
import { BreadcrumbSchema } from '../BreadcrumbSchema';

type ProfileHeroProps = {
  parentLabel: string;
  parentHref: string;
  entityLabel: string;
  title: string;
  description?: string;
  shareLocation: string;
  url: string;
  /** Legacy header: renders the search bar + section tabs. Omit when passing `stats`. */
  activeTab?: GovTradesTab | null;
  search?: React.ReactNode;
  /** Redesigned header: entity media (e.g. company logo) shown beside the title. */
  media?: React.ReactNode;
  /** Redesigned header: a row of meta chips (chamber/party/state, etc.) under the title. */
  meta?: React.ReactNode;
  /** Redesigned header: stat cards rendered in place of the search bar + tabs. */
  stats?: React.ReactNode;
};

export const ProfileHero = ({
  parentLabel,
  parentHref,
  entityLabel,
  title,
  description,
  shareLocation,
  url,
  activeTab,
  search,
  media,
  meta,
  stats,
}: ProfileHeroProps) => {
  const showSearchRow = Boolean(search) || Boolean(activeTab);

  return (
    <section className={styles['hero']} data-testid="gov-trades-profile-hero">
      <BreadcrumbSchema
        items={[
          { name: 'Tools', url: '/tools' },
          { name: 'Government Trades', url: '/government-trades' },
          { name: parentLabel, url: parentHref },
          { name: entityLabel, url },
        ]}
      />
      <div className={styles['wrap']}>
        <nav className={styles['crumb']} aria-label="Breadcrumb">
          <Link href="/tools">Tools</Link> / <Link href="/government-trades">Government Trades</Link> /{' '}
          <Link href={parentHref}>{parentLabel}</Link> / {entityLabel}
          <span className={styles['badge']}>{EdgeBadge}</span>
        </nav>
        <div className={styles['top']}>
          <div className={styles['heading']}>
            {media ? <div className={styles['media']}>{media}</div> : null}
            <h1 className={styles['title']}>{title}</h1>
          </div>
          <ShareButtons
            buttonLocation={shareLocation}
            title={title}
            url={url}
            showButtons={['linkedin', 'twitter', 'facebook', 'email', 'copy']}
            buttonStyle={{
              round: true,
              size: 32,
              bgStyle: { fill: 'rgba(255, 255, 255, 0.08)' },
              iconFillColor: '#9aa9bf',
            }}
          />
        </div>
        {meta ? <div className={styles['meta']}>{meta}</div> : null}
        {description ? <p className={styles['desc']}>{description}</p> : null}
        {stats ? <div className={styles['stats']}>{stats}</div> : null}
        {showSearchRow ? (
          <div className={styles['searchRow']}>
            {search ? <div className={styles['search']}>{search}</div> : null}
            <GovTradesTabs activeTab={activeTab ?? null} />
          </div>
        ) : null}
      </div>
    </section>
  );
};
